/**
 * PlayerCardContentView — the player card's face.
 *
 * Its own directory rather than a second component inside `PlayerCardBaseView`.
 * `single-component-per-file` could not see it there: for an arrow function the
 * rule tests whether the function BODY is a JSX node, and a block body never
 * is, so the block-bodied `PlayerCardBaseView` was not counted as a component
 * and the file looked like it held one. Making that View pure (SE-8019) made
 * both visible.
 * @module components/organisms/custom/ui/PlayerCardContent/PlayerCardContentView
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import PlayerCardFooter from "@/components/organisms/custom/ui/PlayerCardFooter";
import PlayerCardHeader from "@/components/organisms/custom/ui/PlayerCardHeader";
import PlayerCardSignals from "@/components/organisms/custom/ui/PlayerCardSignals";
import PlayerCardTags from "@/components/organisms/custom/ui/PlayerCardTags";

/** Props for {@link PlayerCardContentView}. */
export interface PlayerCardContentViewProps {
  readonly playerName: string;
  readonly isStale?: boolean;
  /** SE-5492: canonical player image URL; null for every player today (SE-5538). */
  readonly imageUrl?: string | null;
  /** SE-5492: explicit position, distinct from the role archetype. */
  readonly position?: string;
  /** SE-5492: total scout reports attached to the player. */
  readonly reportsCount?: number;
  /** SE-5492: latest report's overall score, or null when unscored. */
  readonly verdict?: string | null;
  readonly teamName: string;
  readonly age: number;
  readonly contractMonths: number | null;
  readonly gpr: number;
  /** SE-6490: the backing card's `updatedAt`, driving the footer. */
  readonly updatedAt?: string | null;
  /** SE-6866: the resolved last-editor name completing the footer; null hides it. */
  readonly editorName?: string | null;
  readonly displayedTags: { name: string; color: string }[];
  readonly isHovered?: boolean;
  readonly playerId: string;
  readonly onRemoveTag?: (tagName: string) => void;
  readonly onPreventDrag?: () => void;
  readonly showRemoveButtons?: boolean;
}

/**
 * Renders the card face.
 * @param root0 - Component props
 * @param root0.playerName - Player's display name
 * @param root0.isStale - Whether the card has gone stale in its stage
 * @param root0.imageUrl - SE-5492: canonical player image URL
 * @param root0.position - SE-5492: explicit position (not the role archetype)
 * @param root0.reportsCount - SE-5492: total scout reports attached to the player
 * @param root0.verdict - SE-5492: latest report's overall score, or null
 * @param root0.teamName - Name of player's current club
 * @param root0.age - Player's age in years
 * @param root0.contractMonths - Contract duration in months
 * @param root0.gpr - Gemini Performance Rating score
 * @param root0.updatedAt - SE-6490: the card's last-updated timestamp
 * @param root0.editorName - SE-6866: the resolved last-editor name
 * @param root0.displayedTags - Tags to render as removable chips
 * @param root0.isHovered - Whether the card is hovered
 * @param root0.playerId - Unique identifier for the player
 * @param root0.onRemoveTag - Invoked when a tag is removed, with the tag name
 * @param root0.onPreventDrag - Prevents drag on tag interactions
 * @param root0.showRemoveButtons - Whether the chips expose their remove affordance
 * @returns The card face.
 */
const PlayerCardContentView = ({
  playerName,
  isStale,
  imageUrl,
  position,
  reportsCount,
  verdict,
  teamName,
  age,
  contractMonths,
  gpr,
  updatedAt,
  editorName,
  displayedTags,
  isHovered,
  playerId,
  onRemoveTag,
  onPreventDrag,
  showRemoveButtons,
}: PlayerCardContentViewProps) => (
  <Box
    // SE-6490: the design's card treatment, as tokens. Hover deepens the same
    // hairline instead of the old always-on blue ring plus heavy drop shadow.
    className={`w-full gap-2 rounded-md border bg-surface-base p-3 ${
      isHovered ? "border-outline-strong" : "border-outline-subtle"
    }`}
  >
    <Stack space="2">
      <PlayerCardHeader
        playerName={playerName}
        imageUrl={imageUrl}
        position={position}
        teamName={teamName}
        age={age}
        contractMonths={contractMonths}
        gpr={gpr}
      />

      <PlayerCardSignals
        reportsCount={reportsCount}
        verdict={verdict}
        isStale={isStale}
      />

      <PlayerCardTags
        displayedTags={displayedTags}
        playerId={playerId}
        onRemoveTag={onRemoveTag}
        onPreventDrag={onPreventDrag}
        showRemoveButtons={showRemoveButtons || false}
      />

      <PlayerCardFooter updatedAt={updatedAt} editorName={editorName} />
    </Stack>
  </Box>
);

PlayerCardContentView.displayName = "PlayerCardContentView";

export default memo(PlayerCardContentView);
